import { cva } from 'class-variance-authority';
import {
  AlertCircle,
  Check,
  ChevronDown,
  ChevronRight,
  Loader,
  Pause,
} from 'lucide-react';
import * as React from 'react';
import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { assertNever, cn } from '@/lib/utils';
import { formatAIDuration } from './format-ai-duration';

export type WorkflowStepStatus =
  | 'pending'
  | 'running'
  | 'suspended'
  | 'completed'
  | 'error';

export type WorkflowStepAppearance = 'contained' | 'embedded' | 'timeline';

export interface WorkflowStepViewModel {
  id: string;
  title: string;
  description?: string;
  contextLabel?: string;
  status: WorkflowStepStatus;
  content?: React.ReactNode;
  durationMs?: number;
  error?: string;
}

export interface WorkflowStepRendererProps {
  steps: readonly WorkflowStepViewModel[];
  appearance?: WorkflowStepAppearance;
  itemSlot?: string;
  expandable?: boolean;
  activeStepId?: string;
  expandedStepIds?: readonly string[];
  defaultExpandedStepIds?: readonly string[];
  onExpandedStepIdsChange?: (ids: readonly string[]) => void;
  className?: string;
}

interface PrivateWorkflowStepRendererProps extends WorkflowStepRendererProps {
  renderItems?: boolean;
}

const workflowStepItemStatusClasses = {
  pending: 'text-secondary-foreground',
  running: 'text-primary',
  suspended: 'text-warning',
  completed: 'text-success',
  error: 'text-destructive',
} as const satisfies Record<WorkflowStepStatus, string>;

const workflowStepIconStatusClasses = {
  pending: 'bg-accent text-muted-foreground',
  running: 'bg-primary/20 text-primary',
  suspended: 'bg-warning/20 text-warning',
  completed: 'bg-success/20 text-success',
  error: 'bg-destructive/20 text-destructive',
} as const satisfies Record<WorkflowStepStatus, string>;

export const workflowStepItemVariants = cva(
  'flex items-start gap-3 px-4 py-3 transition-colors motion-reduce:transition-none',
  {
    variants: {
      status: workflowStepItemStatusClasses,
    },
    defaultVariants: {
      status: 'pending',
    },
  },
);

export const workflowStepIconVariants = cva(
  'size-5 shrink-0 flex items-center justify-center rounded-full',
  {
    variants: {
      status: workflowStepIconStatusClasses,
    },
    defaultVariants: {
      status: 'pending',
    },
  },
);

const workflowStepAppearanceConfig = {
  contained: {
    rootClassName: 'relative',
    hasDivider: true,
    hasConnector: false,
  },
  embedded: {
    rootClassName: 'relative',
    hasDivider: true,
    hasConnector: false,
  },
  timeline: {
    rootClassName: 'relative',
    hasDivider: false,
    hasConnector: true,
  },
} as const satisfies Record<
  WorkflowStepAppearance,
  { rootClassName: string; hasDivider: boolean; hasConnector: boolean }
>;

export function normalizeExpandedWorkflowStepIds(
  ids: readonly string[],
  steps: readonly WorkflowStepViewModel[],
): string[] {
  const requested = new Set(ids);
  return steps.flatMap((step) => (requested.has(step.id) ? [step.id] : []));
}

function areWorkflowStepIdsEqual(
  left: readonly string[],
  right: readonly string[],
): boolean {
  return (
    left.length === right.length &&
    left.every((id, index) => id === right[index])
  );
}

function isIterableWorkflowStepContent(
  content: React.ReactNode,
): content is Iterable<React.ReactNode> {
  return (
    typeof content === 'object' &&
    content !== null &&
    Symbol.iterator in content &&
    typeof (content as Iterable<React.ReactNode>)[Symbol.iterator] ===
      'function'
  );
}

function isIteratorWorkflowStepContent(
  content: unknown,
): content is Iterator<React.ReactNode> {
  return (
    typeof (content as Partial<Iterator<React.ReactNode>>).next === 'function'
  );
}

interface NormalizedWorkflowStepContent {
  content: React.ReactNode;
  hasRenderableContent: boolean;
  changed: boolean;
}

const normalizedOneShotWorkflowStepContent = new WeakMap<
  object,
  NormalizedWorkflowStepContent
>();

function normalizeIterableWorkflowStepContent(
  content: Iterable<React.ReactNode>,
  iterator: Iterator<React.ReactNode>,
): NormalizedWorkflowStepContent {
  const normalizedChildren: React.ReactNode[] = [];
  let hasRenderableContent = false;
  let changed = !Array.isArray(content);

  for (let result = iterator.next(); !result.done; result = iterator.next()) {
    const normalizedChild = normalizeWorkflowStepContent(result.value);
    normalizedChildren.push(normalizedChild.content);
    hasRenderableContent ||= normalizedChild.hasRenderableContent;
    changed ||= normalizedChild.changed;
  }

  return {
    content: changed ? normalizedChildren : content,
    hasRenderableContent,
    changed,
  };
}

function normalizeWorkflowStepContent(
  content: React.ReactNode,
): NormalizedWorkflowStepContent {
  if (
    content === null ||
    content === undefined ||
    typeof content === 'boolean' ||
    content === ''
  ) {
    return { content, hasRenderableContent: false, changed: false };
  }

  if (
    typeof content === 'string' ||
    typeof content === 'number' ||
    typeof content === 'bigint'
  ) {
    return { content, hasRenderableContent: true, changed: false };
  }

  if (React.isValidElement<{ children?: React.ReactNode }>(content)) {
    if (content.type !== React.Fragment) {
      return { content, hasRenderableContent: true, changed: false };
    }

    const normalizedChildren = normalizeWorkflowStepContent(
      content.props.children,
    );
    return {
      content: normalizedChildren.changed
        ? React.cloneElement(content, undefined, normalizedChildren.content)
        : content,
      hasRenderableContent: normalizedChildren.hasRenderableContent,
      changed: normalizedChildren.changed,
    };
  }

  if (isIterableWorkflowStepContent(content)) {
    const iterator = content[Symbol.iterator]();
    const oneShotCacheKey = isIteratorWorkflowStepContent(iterator)
      ? (iterator as object)
      : null;

    if (oneShotCacheKey !== null) {
      const cached = normalizedOneShotWorkflowStepContent.get(oneShotCacheKey);
      if (cached !== undefined) return cached;
    }

    const normalized = normalizeIterableWorkflowStepContent(content, iterator);
    if (oneShotCacheKey !== null) {
      normalizedOneShotWorkflowStepContent.set(oneShotCacheKey, normalized);
    }
    return normalized;
  }

  return { content, hasRenderableContent: true, changed: false };
}

function getNonBlankWorkflowStepError(
  error: string | undefined,
): string | undefined {
  return error?.trim() ? error : undefined;
}

function getWorkflowStepStatusLabel(status: WorkflowStepStatus): string {
  switch (status) {
    case 'pending':
      return 'Pending';
    case 'running':
      return 'Running';
    case 'suspended':
      return 'Paused';
    case 'completed':
      return 'Completed';
    case 'error':
      return 'Error';
    default:
      return assertNever(status);
  }
}

export interface WorkflowStepStatusIconProps {
  status: WorkflowStepStatus;
  index: number;
  className?: string;
}

/** Longest transition (duration + delay) on `el`, in ms; 0 when none runs. */
function getTransitionMs(el: Element): number {
  const cs = el.ownerDocument.defaultView?.getComputedStyle(el);
  if (!cs) return 0;
  // `transition-none` leaves the duration set; only the property is 'none'.
  if (cs.transitionProperty.split(',').every((v) => v.trim() === 'none'))
    return 0;
  const toMs = (list: string) =>
    list.split(',').map((v) => {
      const n = Number.parseFloat(v);
      if (Number.isNaN(n)) return 0;
      return v.trim().endsWith('ms') ? n : n * 1000;
    });
  const durations = toMs(cs.transitionDuration);
  const delays = toMs(cs.transitionDelay);
  return Math.max(
    0,
    ...durations.map((d, i) => d + (delays[i % delays.length] ?? 0)),
  );
}

function WorkflowStepStatusGlyph({
  status,
  index,
  current,
}: {
  status: WorkflowStepStatus;
  index: number;
  current: boolean;
}) {
  switch (status) {
    case 'pending':
      return <span className="text-xs font-medium">{index + 1}</span>;
    case 'running':
      return (
        <Loader
          className={cn(
            'size-3',
            current && 'animate-spin motion-reduce:animate-none',
          )}
        />
      );
    case 'suspended':
      return <Pause className="size-3" />;
    case 'completed':
      return <Check className="size-3" />;
    case 'error':
      return <AlertCircle className="size-3" />;
    default:
      return assertNever(status);
  }
}

/**
 * Status icon. A status change crossfades the glyphs (opacity transition, so a
 * reversal retargets from the current value); the circle colour follows on the
 * same token. No scale, bounce, shake or surface pulse; running is one spin.
 */
export function WorkflowStepStatusIcon({
  status,
  index,
  className,
}: WorkflowStepStatusIconProps) {
  const rootRef = React.useRef<HTMLDivElement>(null);
  // Re-evaluated when the preference flips, so an in-flight fade ends at once.
  const reducedMotion = useReducedMotion();
  const [trail, setTrail] = React.useState<readonly WorkflowStepStatus[]>([
    status,
  ]);
  const [seen, setSeen] = React.useState(status);
  // The glyph present at first mount appears at once; later ones fade in.
  const initialGlyph = React.useRef<WorkflowStepStatus | null>(status);
  if (seen !== status) {
    setSeen(status);
    setTrail((t) => [status, ...t.filter((s) => s !== status)]);
  }

  const settle = React.useCallback(() => {
    if (initialGlyph.current !== status) initialGlyph.current = null;
    setTrail((t) => (t.length > 1 ? t.slice(0, 1) : t));
  }, [status]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-check when the motion preference flips mid-fade
  React.useLayoutEffect(() => {
    const root = rootRef.current;
    if (trail.length < 2 || !root) return;
    const ms = getTransitionMs(root);
    if (ms <= 0) {
      settle();
      return;
    }
    const timer = setTimeout(settle, ms + 100);
    return () => clearTimeout(timer);
  }, [trail, settle, reducedMotion]);

  return (
    <div
      ref={rootRef}
      data-slot="workflow-step-icon"
      className={cn(
        workflowStepIconVariants({ status }),
        'relative transition-colors duration-(--duration-base) ease-smooth motion-reduce:transition-none',
        className,
      )}
    >
      {trail.map((glyph) => {
        const current = glyph === status;
        return (
          <span
            key={glyph}
            data-slot="workflow-step-glyph"
            data-current={current ? 'true' : undefined}
            aria-hidden={current ? undefined : 'true'}
            onTransitionEnd={(event) => {
              if (
                !current &&
                event.target === event.currentTarget &&
                event.propertyName === 'opacity'
              ) {
                setTrail((t) => t.filter((s) => s === status));
                if (initialGlyph.current === glyph) initialGlyph.current = null;
              }
            }}
            onTransitionCancel={(event) => {
              if (!current && event.target === event.currentTarget) settle();
            }}
            className={cn(
              'absolute inset-0 grid place-items-center transition-opacity duration-(--duration-base) ease-smooth motion-reduce:transition-none',
              current ? 'opacity-100' : 'pointer-events-none opacity-0',
              initialGlyph.current !== glyph && 'starting:opacity-0',
            )}
          >
            <WorkflowStepStatusGlyph
              status={glyph}
              index={index}
              current={current}
            />
          </span>
        );
      })}
    </div>
  );
}

WorkflowStepStatusIcon.displayName = 'WorkflowStepStatusIcon';

export interface WorkflowDisclosureProps {
  open: boolean;
  /** Control that owns the panel; receives focus if it is inside on close. */
  triggerRef: React.RefObject<HTMLElement | null>;
  id?: string;
  'data-slot'?: string;
  children: React.ReactNode;
}

/**
 * Natural-height disclosure: grid-rows 0fr/1fr, 200 ms open, 120 ms close.
 * Stays mounted while closing (inert at once), then unmounts so a closed panel
 * has no layout box. Without a running transition (reduced motion, no CSS) it
 * unmounts immediately.
 */
export function WorkflowDisclosure({
  open,
  triggerRef,
  id,
  'data-slot': dataSlot = 'workflow-step-panel',
  children,
}: WorkflowDisclosureProps) {
  const panelRef = React.useRef<HTMLDivElement>(null);
  // True while focus is inside the panel, including when the focused node is
  // removed with the panel content (removal leaves activeElement on body).
  const focusInsideRef = React.useRef(false);
  const reducedMotion = useReducedMotion();
  const [present, setPresent] = React.useState(open);
  // Only a panel created after first mount grows from 0fr; one that is open
  // at mount appears at natural size.
  const [enterFromClosed, setEnterFromClosed] = React.useState(false);
  if (open && !present) {
    setPresent(true);
    setEnterFromClosed(true);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-check when the motion preference flips mid-close
  React.useLayoutEffect(() => {
    const panel = panelRef.current;
    if (open || !panel) return;
    const active = panel.ownerDocument.activeElement;
    const lost = focusInsideRef.current && active === panel.ownerDocument.body;
    focusInsideRef.current = false;
    if (lost || (active && panel.contains(active))) {
      triggerRef.current?.focus({ preventScroll: true });
    }
    const ms = getTransitionMs(panel);
    if (ms <= 0) {
      setPresent(false);
      return;
    }
    const timer = setTimeout(() => setPresent(false), ms + 100);
    return () => clearTimeout(timer);
  }, [open, triggerRef, reducedMotion]);

  if (!present) return null;

  const close = (event: React.TransitionEvent<HTMLDivElement>) => {
    if (
      !open &&
      event.target === event.currentTarget &&
      event.propertyName === 'grid-template-rows'
    ) {
      setPresent(false);
    }
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: focus tracking only, not an interactive control
    <div
      ref={panelRef}
      id={id}
      data-slot={dataSlot}
      data-state={open ? 'open' : 'closed'}
      inert={!open}
      onTransitionEnd={close}
      onTransitionCancel={close}
      onFocus={() => {
        focusInsideRef.current = true;
      }}
      onBlur={(event) => {
        if (event.target.isConnected) focusInsideRef.current = false;
      }}
      className={cn(
        'grid ease-smooth transition-[grid-template-rows] motion-reduce:transition-none',
        open
          ? 'grid-rows-[1fr] duration-(--duration-moderate)'
          : 'grid-rows-[0fr] duration-(--duration-fast)',
        enterFromClosed && 'starting:grid-rows-[0fr]',
      )}
    >
      <div
        className={cn(
          'min-h-0 overflow-hidden transition-[display] transition-discrete motion-reduce:transition-none',
          open
            ? 'duration-(--duration-moderate)'
            : 'hidden duration-(--duration-fast)',
        )}
      >
        {children}
      </div>
    </div>
  );
}

WorkflowDisclosure.displayName = 'WorkflowDisclosure';

export interface WorkflowStepItemProps {
  step: WorkflowStepViewModel;
  index: number;
  isLast: boolean;
  appearance?: WorkflowStepAppearance;
  itemSlot?: string;
  expandable?: boolean;
  expanded: boolean;
  active?: boolean;
  onExpandedChange: () => void;
}

export function WorkflowStepItem({
  step,
  index,
  isLast,
  appearance = 'contained',
  itemSlot = 'workflow-step-item',
  expandable = true,
  expanded,
  active = false,
  onExpandedChange,
}: WorkflowStepItemProps) {
  const contentId = React.useId();
  const errorDescriptionId = React.useId();
  const normalizedContent = normalizeWorkflowStepContent(step.content);
  const hasContent = normalizedContent.hasRenderableContent;
  const error = getNonBlankWorkflowStepError(step.error);
  const hasError = error !== undefined;
  const hasExpandableDetails = hasContent || hasError;
  const isDisclosureControl = hasExpandableDetails && expandable;
  const triggerRef = React.useRef<HTMLElement>(null);
  const appearanceConfig = workflowStepAppearanceConfig[appearance];
  const stepContent = (
    <>
      <WorkflowStepStatusIcon status={step.status} index={index} />

      <div className="min-w-0 flex-1 text-start">
        <div className="flex flex-wrap items-center gap-2">
          {step.contextLabel && (
            <span
              data-slot="workflow-step-context"
              className="rounded-full border border-border px-2 py-0.5 text-[0.6875rem] font-medium leading-none text-secondary-foreground"
            >
              {step.contextLabel}
            </span>
          )}
          <span
            className={cn(
              'text-sm font-medium',
              step.status === 'pending'
                ? 'text-secondary-foreground'
                : 'text-foreground',
            )}
          >
            {step.title}
          </span>
          <span className="text-xs text-secondary-foreground">
            {getWorkflowStepStatusLabel(step.status)}
          </span>
          {step.durationMs !== undefined && (
            <span className="text-xs text-secondary-foreground">
              {formatAIDuration(step.durationMs)}
            </span>
          )}
        </div>
        {step.description && (
          <p className="mt-0.5 text-xs text-secondary-foreground">
            {step.description}
          </p>
        )}
      </div>

      {isDisclosureControl && (
        <div className="shrink-0">
          {expanded ? (
            <ChevronDown className="size-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="size-4 text-muted-foreground rtl:rotate-180" />
          )}
        </div>
      )}
    </>
  );

  return (
    <div
      data-slot={itemSlot}
      data-step-status={step.status}
      data-appearance={appearance}
      className={cn(
        appearanceConfig.rootClassName,
        appearanceConfig.hasDivider && !isLast && 'border-b border-border',
      )}
    >
      {isDisclosureControl ? (
        <button
          ref={triggerRef as React.RefObject<HTMLButtonElement>}
          type="button"
          onClick={onExpandedChange}
          aria-current={active ? 'step' : undefined}
          aria-expanded={expanded}
          aria-controls={contentId}
          aria-describedby={hasError ? errorDescriptionId : undefined}
          className={cn(
            workflowStepItemVariants({ status: step.status }),
            'w-full cursor-pointer hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring',
          )}
        >
          {stepContent}
        </button>
      ) : (
        // biome-ignore lint/a11y/useSemanticElements: fieldset is form-only; this is a named focus target
        <div
          ref={triggerRef as React.RefObject<HTMLDivElement>}
          // Stable focus destination if details vanish while focus is in the panel.
          tabIndex={-1}
          role="group"
          aria-label={step.title}
          aria-current={active ? 'step' : undefined}
          aria-describedby={hasError ? errorDescriptionId : undefined}
          className={cn(
            workflowStepItemVariants({ status: step.status }),
            'w-full',
          )}
        >
          {stepContent}
        </div>
      )}

      <WorkflowDisclosure
        open={expanded && hasExpandableDetails}
        id={contentId}
        triggerRef={triggerRef}
      >
        <div className="px-4 pb-3 ps-12">
          {hasError && (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="mt-0.5 size-4 shrink-0 text-destructive" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            </div>
          )}
          {hasContent && (
            <div className={hasError ? 'mt-3' : undefined}>
              {normalizedContent.content}
            </div>
          )}
        </div>
      </WorkflowDisclosure>

      {hasError && (
        <span id={errorDescriptionId} className="sr-only">
          {error}
        </span>
      )}

      {appearanceConfig.hasConnector && !isLast && (
        <div
          data-slot="workflow-step-connector"
          aria-hidden="true"
          className="absolute top-8 -bottom-3 start-6 w-px bg-border"
        />
      )}
    </div>
  );
}

WorkflowStepItem.displayName = 'WorkflowStepItem';

export function WorkflowStepRenderer({
  steps,
  appearance = 'contained',
  itemSlot = 'workflow-step-item',
  expandable = true,
  activeStepId,
  expandedStepIds: controlledExpandedStepIds,
  defaultExpandedStepIds = [],
  onExpandedStepIdsChange,
  className,
  renderItems = true,
}: PrivateWorkflowStepRendererProps) {
  const isControlled = controlledExpandedStepIds !== undefined;
  const controlledSnapshot = isControlled
    ? [...controlledExpandedStepIds]
    : undefined;
  const [uncontrolledExpandedStepIds, setUncontrolledExpandedStepIds] =
    React.useState<readonly string[]>(() =>
      normalizeExpandedWorkflowStepIds(defaultExpandedStepIds, steps),
    );
  const lastControlledExpandedStepIdsRef = React.useRef<readonly string[]>(
    controlledSnapshot ?? [],
  );
  const wasControlledRef = React.useRef(isControlled);
  const rawExpandedStepIds = isControlled
    ? controlledExpandedStepIds
    : uncontrolledExpandedStepIds;
  const visibleExpandedStepIds = normalizeExpandedWorkflowStepIds(
    rawExpandedStepIds,
    steps,
  );
  const expandedSet = new Set(visibleExpandedStepIds);

  React.useLayoutEffect(() => {
    if (controlledSnapshot !== undefined) {
      lastControlledExpandedStepIdsRef.current = controlledSnapshot;
    }
  }, [controlledSnapshot]);

  React.useLayoutEffect(() => {
    const wasControlled = wasControlledRef.current;
    wasControlledRef.current = isControlled;
    if (isControlled) return;

    setUncontrolledExpandedStepIds((current) => {
      const source = wasControlled
        ? lastControlledExpandedStepIdsRef.current
        : current;
      const reconciled = normalizeExpandedWorkflowStepIds(source, steps);
      return areWorkflowStepIdsEqual(reconciled, current)
        ? current
        : reconciled;
    });
  }, [isControlled, steps]);

  const toggleStep = (id: string) => {
    const next = new Set(visibleExpandedStepIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    const normalizedNext = normalizeExpandedWorkflowStepIds([...next], steps);
    if (!isControlled) {
      setUncontrolledExpandedStepIds([...normalizedNext]);
    }
    onExpandedStepIdsChange?.([...normalizedNext]);
  };

  if (!renderItems) return null;

  return (
    <ol
      data-slot="workflow-step-list"
      data-appearance={appearance}
      className={cn('m-0 list-none p-0', className)}
    >
      {steps.map((step, index) => (
        <li key={step.id}>
          <WorkflowStepItem
            step={step}
            index={index}
            isLast={index === steps.length - 1}
            appearance={appearance}
            itemSlot={itemSlot}
            expandable={expandable}
            expanded={expandedSet.has(step.id)}
            active={activeStepId === step.id}
            onExpandedChange={() => toggleStep(step.id)}
          />
        </li>
      ))}
    </ol>
  );
}

WorkflowStepRenderer.displayName = 'WorkflowStepRenderer';
